import moment from "moment";

/**
 * Formats a date into a specified string format.
 * @param {any} date - The date value to format (can be a string, Date object, or moment-compatible value).
 * @param {string} format - The moment.js format string (e.g., "YYYY-MM-DD", "MM/DD/YYYY").
 * @param {string} [fallback=""] - The fallback value to return if the date is invalid or falsy.
 * @returns {string} The formatted date string, or the fallback value if the date is invalid.
 * @example
 * formatDate("2024-01-15", "MM/DD/YYYY") // Returns "01/15/2024"
 * formatDate(null, "YYYY-MM-DD", "N/A") // Returns "N/A"
 */
export const formatDate = (date: any, format: string, fallback: string = "") =>
  date && moment(date).isValid() ? moment(date).format(format) : fallback;

/**
 * Returns the local calendar date for a `Date` in `YYYY-MM-DD` format.
 *
 * Unlike `toISOString().split("T")[0]`, this uses the viewer's local
 * timezone so the date aligns with `Date#getTimezoneOffset()` payloads.
 *
 * @param date - Date to format; defaults to the current local time.
 * @returns Local calendar date string.
 */
export const getLocalCalendarDate = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Takes the UTC calendar day from an ISO-8601 timestamp.
 *
 * `2026-08-18T09:14:22.000Z` becomes `2026-08-18`. Times on the same UTC
 * day share one key. Prefer {@link getLocalCalendarDate} when the viewer's
 * local day is required.
 *
 * @param isoTimestamp - ISO-8601 timestamptz, or empty/undefined
 * @returns UTC `YYYY-MM-DD`, or an empty string when missing
 */
export const toUtcDateKey = (isoTimestamp: string | undefined): string =>
  isoTimestamp?.slice(0, 10) ?? "";

/**
 * Local date and timezone offset for denial/query bucket API payloads.
 *
 * @param date - Reference instant; defaults to the current local time.
 * @returns `date` in local `YYYY-MM-DD` form and `time_offset` in minutes.
 */
export const getLocalDenialQueryBucketTimeContext = (
  date: Date = new Date(),
): { date: string; time_offset: number } => ({
  date: getLocalCalendarDate(date),
  time_offset: date.getTimezoneOffset(),
});

/**
 * Formats an ISO or calendar date string for coverage UI display (MM/DD/YYYY).
 *
 * @param iso - Date string in YYYY-MM-DD or ISO-8601 form, or empty when unknown.
 * @returns Formatted date, the original string if parsing fails, or an em dash when empty.
 */
export const formatDisplayDate = (iso: string): string => {
  if (!iso) return "—";
  const m = moment(iso, ["YYYY-MM-DD", moment.ISO_8601], true);
  if (!m.isValid()) return iso;
  return m.format("MM/DD/YYYY");
};

/**
 * Checks if two date strings represent the same calendar day.
 *
 * Comparison is performed in UTC so the result is independent of the
 * viewer's local timezone. Inputs may be plain calendar dates
 * (e.g. "YYYY-MM-DD", "MM/DD/YYYY") or full ISO-8601 timestamps;
 * in all cases only the year/month/day components are compared.
 *
 * @param {string} [date1] - The first date string to compare.
 * @param {string} [date2] - The second date string to compare.
 * @returns {boolean} True if both dates are valid and fall on the same UTC calendar day, false otherwise.
 * @example
 * checkIfDatesAreSame("2024-01-15", "2024-01-15") // Returns true
 * checkIfDatesAreSame("2024-01-15", "2024-01-16") // Returns false
 * checkIfDatesAreSame(undefined, "2024-01-15") // Returns false
 * checkIfDatesAreSame("2024-01-15T23:00:00Z", "2024-01-15") // Returns true regardless of local TZ
 */
export const checkIfDatesAreSame = (date1?: string, date2?: string) => {
  const m1 = moment.utc(
    date1,
    ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601],
    false,
  );
  const m2 = moment.utc(
    date2,
    ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601],
    false,
  );
  if (!m1.isValid() || !m2.isValid()) {
    return false;
  }
  return m1.isSame(m2, "day");
};

/**
 * Parses a date string in MM/DD/YYYY format into a JavaScript Date object.
 *
 * @param dateString - The date string to parse in MM/DD/YYYY format (e.g., "01/15/2024")
 * @returns A Date object representing the parsed date
 *
 * @example
 * parseMMDDYYYY("01/15/2024") // Returns Date object for January 15, 2024
 * parseMMDDYYYY("12/31/2023") // Returns Date object for December 31, 2023
 *
 * @remarks
 * This function assumes the input string is properly formatted.
 * Invalid or malformed strings may result in an Invalid Date.
 */
export const parseMMDDYYYY = (dateString: string): Date => {
  const [month, day, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Matches auth dates written in `M-D-YYYY` / `MM-DD-YYYY` form.
 *
 * Only 4-digit-year, hyphen-separated dates with a valid month (1–12) and day
 * (1–31) are captured, so ISO dates (`YYYY-MM-DD`), non-date hyphenated tokens
 * (e.g. J-code ranges), and lookalike numeric identifiers (e.g. record numbers
 * like `12-34-5678`) are left untouched. Constraining the ranges avoids the
 * silent data-corruption risk on this write-back path.
 */
const HYPHENATED_AUTH_DATE_REGEX =
  /\b(0?[1-9]|1[0-2])-(0?[1-9]|[12]\d|3[01])-(\d{4})\b/g;

/**
 * Normalizes hyphenated auth dates inside a free-text blob to slash format.
 *
 * Fulfillment users copy Business Office / Auth-on-File notes straight into the
 * DB, which only accepts `MM/DD/YYYY`. Portal / RISA output sometimes uses
 * `MM-DD-YYYY`, forcing a manual hyphen→slash fix. This rewrites every such
 * date in the text (e.g. `6-10-2003` → `6/10/2003`) while leaving already
 * slash-formatted dates, ISO dates, and other hyphenated tokens unchanged.
 *
 * @param {string} [text] - The note text to normalize.
 * @returns {string} The text with hyphenated dates converted to slash format.
 * @example
 * normalizeAuthDatesToSlashFormat("Auth from 6-10-2003 to 6-10-2004")
 * // Returns "Auth from 6/10/2003 to 6/10/2004"
 */
export const normalizeAuthDatesToSlashFormat = (text?: string): string => {
  if (!text) return text ?? "";
  return text.replace(HYPHENATED_AUTH_DATE_REGEX, "$1/$2/$3");
};

/**
 * Formats a date string to UTC and returns it in the specified format.
 * @param {string} date - The date string to format.
 * @param {string} format - The moment.js format string (e.g., "YYYY-MM-DD", "MM/DD/YYYY HH:mm:ss").
 * @param {string} [fallback=""] - The fallback value to return if the date is invalid.
 * @returns {string} The formatted UTC date string, or the fallback value if the date is invalid.
 * @example
 * formatDateToUTCWithFormat("2024-01-15T10:30:00-05:00", "YYYY-MM-DD HH:mm:ss") // Returns "2024-01-15 15:30:00" (UTC)
 * formatDateToUTCWithFormat("invalid-date", "YYYY-MM-DD", "N/A") // Returns "N/A"
 */
export const formatDateToUTCWithFormat = (
  date: string,
  format: string,
  fallback: string = "",
): string => {
  if (!date) return fallback;
  return moment(date).isValid() ? moment.utc(date).format(format) : fallback;
};
